import apiUtils from '@elab/core/utils/apiUtils';

import HTTPClient from '../../utils/HTTPClient';

import { getActiveFilters } from './helpers';

const getApiService = (productCode, isAvis, isPaginated) => {
    const defaultApiType = isPaginated ? 'paginatedReviews' : 'feefoReviews';
    const apiType = isAvis ? 'avisReviews' : defaultApiType;
    return `${apiUtils.getBaseUri()}/products/${productCode}/${apiType}`;
};

const loadReviews = async ({
    productCode,
    pageSize,
    currentPage,
    sort,
    activeFilters,
    isAvis,
    isPaginated,
    isMultipleFilters,
}) => {
    const params = new URLSearchParams();
    const filterStr = getActiveFilters(activeFilters, isMultipleFilters);

    if (filterStr) {
        params.append(isMultipleFilters ? 'query' : 'rating', filterStr);
    }

    if (pageSize) {
        params.append('pageSize', pageSize);
    }
    if (currentPage) {
        params.append('currentPage', currentPage);
    }
    if (sort) {
        params.append('sort', sort);
    }

    const response = await HTTPClient().get(
        `${getApiService(productCode, isAvis, isPaginated)}?${params.toString()}`,
    );

    if (!response.ok) {
        throw new Error(`Unable to load reviews (${response.status})`);
    }
    return await response.json();
};

const reportReview = async id => {
    const endpoint = `${apiUtils.getBaseUri()}/product-reviews/report/${id}`;
    const response = await HTTPClient().postJson(endpoint);

    if (!response.ok) {
        throw new Error(`Unable to report review (${response.status})`);
    }
};

const voteForReview = async (id, vote) => {
    const params = `useful=${vote}`;
    const uri = `${apiUtils.getBaseUri()}/product-reviews/${id}/useful`;

    const response = await HTTPClient().put(uri, params);

    if (!response.ok) {
        throw new Error(`Unable to vote for review (${response.status})`);
    }
};

const reviewAddReaction = async (type, id) => {
    const apiType = type === 'like' ? 'addLike' : 'addDislike';
    const endpoint = `${apiUtils.getBaseUri()}/enhance-reviews/${apiType}/${id}`;
    const response = await HTTPClient().postJson(endpoint);

    if (!response.ok) {
        throw new Error(`Unable to report review (${response.status})`);
    }
};
const reviewRemoveReaction = async (type, id) => {
    const apiType = type === 'like' ? 'removeLike' : 'removeDislike';
    const endpoint = `${apiUtils.getBaseUri()}/enhance-reviews/${apiType}/${id}`;
    const response = await HTTPClient().postJson(endpoint);

    if (!response.ok) {
        throw new Error(`Unable to report review (${response.status})`);
    }
};

const defineCommentsLanguages = async ({ comments, googleApiKey }) => {
    const url = `https://translation.googleapis.com/language/translate/v2/detect?key=${googleApiKey}`;
    const body = {
        q: comments,
    };
    const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        throw new Error('Unable to detect comments language');
    }
    const result = await response.json();
    return result.data.detections.map(item => item[0].language);
};

const translateReview = async ({ comment, headline, browserLanguage, googleApiKey }) => {
    const url = `https://translation.googleapis.com/language/translate/v2?key=${googleApiKey}`;
    const body = {
        q: [comment, headline],
        target: browserLanguage,
    };
    const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        throw new Error('Unable to translate review');
    }
    const result = await response.json();

    return {
        comment: result.data.translations[0].translatedText,
        headline: result.data.translations[1].translatedText,
    };
};

const reportEnhancedReview = async id => {
    const endpoint = `${apiUtils.getBaseUri()}/enhance-reviews/${id}/report`;
    const response = await HTTPClient().postJson(endpoint);

    if (!response.ok) {
        throw new Error(`Unable to report enhanced review (${response.status})`);
    }
};

const getProductReviewVerifiedPurchaser = async productCode => {
    const uri = `${apiUtils.getBaseUri()}/enhance-reviews/${productCode}/verified-purchase`;
    const response = await HTTPClient().get(uri);
    if (!response.ok) {
        throw new Error(`Unable to get purchaser status (${response.status})`);
    }
    return await response.text();
};

export default {
    loadReviews,
    reportReview,
    voteForReview,
    reviewAddReaction,
    reviewRemoveReaction,
    defineCommentsLanguages,
    translateReview,
    reportEnhancedReview,
    getProductReviewVerifiedPurchaser,
};
